/* PDP sections — shared behavior. Safe to load more than once. */
(() => {
  if (window.__pdpLoaded) return;
  window.__pdpLoaded = true;

  const money = (cents, format) => {
    const fmt = format || window.pdpMoneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const noZeros = value.replace(/\.00$/, '');
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, noZeros);
  };

  /* Gallery: thumbnails, arrows, active state */
  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pdp-gallery__track');
      this.thumbs = [...this.querySelectorAll('.pdp-gallery__thumb')];
      this.thumbList = this.querySelector('.pdp-gallery__thumbs');
      if (!this.track) return;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.querySelectorAll('[data-gal-prev]').forEach((b) => b.addEventListener('click', () => this.go(this.index - 1)));
      this.querySelectorAll('[data-gal-next]').forEach((b) => b.addEventListener('click', () => this.go(this.index + 1)));
      this.index = 0;
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.setActive(i);
        });
      });
    }
    go(i) {
      const count = this.track.children.length;
      if (!count) return;
      i = (i + count) % count;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
      this.setActive(i);
    }
    setActive(i) {
      this.index = i;
      this.thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
      const thumb = this.thumbs[i];
      if (thumb && this.thumbList) {
        const vertical = getComputedStyle(this.thumbList).flexDirection === 'column';
        const list = this.thumbList;
        if (vertical) list.scrollTo({ top: thumb.offsetTop - list.clientHeight / 2 + thumb.clientHeight / 2, behavior: 'smooth' });
        else list.scrollTo({ left: thumb.offsetLeft - list.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
      }
    }
  }
  if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);

  /* Buy box: bundle tiers + add to cart (+ free gifts) */
  class PdpBuyBox extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-pdp-atc]');
      this.priceEl = this.querySelector('[data-pdp-atc-price]');
      this.compareEl = this.querySelector('[data-pdp-atc-compare]');
      this.error = this.querySelector('[data-pdp-error]');
      this.querySelectorAll('input[name="pdp-tier"]').forEach((r) => r.addEventListener('change', () => this.update()));
      this.button?.addEventListener('click', () => this.add());
      this.querySelectorAll('[data-pdp-upsell]').forEach((b) => b.addEventListener('click', () => this.addUpsell(b)));
      this.update();
    }
    tier() {
      return this.querySelector('input[name="pdp-tier"]:checked');
    }
    update() {
      const t = this.tier();
      const price = t ? +t.dataset.price : +this.dataset.price;
      const compare = t ? +t.dataset.compare : +this.dataset.compare;
      if (this.priceEl) this.priceEl.textContent = money(price);
      if (this.compareEl) {
        this.compareEl.textContent = compare > price ? money(compare) : '';
        this.compareEl.hidden = !(compare > price);
      }
    }
    async add() {
      const t = this.tier();
      const qty = t ? +t.dataset.qty : 1;
      const items = [{ id: +this.dataset.variant, quantity: qty }];
      this.querySelectorAll('[data-pdp-gift]').forEach((g) => {
        if (g.dataset.variant) items.push({ id: +g.dataset.variant, quantity: 1, properties: { _pdp_gift: 'true' } });
      });
      await this.post(items, this.button);
    }
    async addUpsell(btn) {
      await this.post([{ id: +btn.dataset.variant, quantity: 1 }], btn, false);
    }
    async post(items, btn, redirect = true) {
      if (!items[0].id) return this.fail('Pick a product in the theme editor first.');
      btn.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart');
        }
        if (redirect && this.dataset.redirect === 'drawer' && customElements.get('rm-cart-drawer')) {
          btn.removeAttribute('aria-busy');
          document.dispatchEvent(new CustomEvent('rm:cart:open'));
        } else if (redirect) window.location.href = this.dataset.redirect === 'checkout' ? '/checkout' : '/cart';
        else {
          btn.removeAttribute('aria-busy');
          btn.querySelector('[data-label]')?.replaceChildren(document.createTextNode(btn.dataset.added || 'ADDED'));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (e) {
        btn.removeAttribute('aria-busy');
        this.fail(e.message);
      }
    }
    fail(msg) {
      if (!this.error) return;
      this.error.textContent = msg;
      this.error.hidden = false;
    }
  }
  if (!customElements.get('pdp-buy-box')) customElements.define('pdp-buy-box', PdpBuyBox);

  // Sticky buy bar: shows once the Add to Cart button has scrolled above the screen,
  // and scrolls back to the bundles (or the button) when tapped.
  class PdpStickyCta extends HTMLElement {
    connectedCallback() {
      const section = this.closest('section');
      this.anchor = section?.querySelector('.pdp-buy');
      this.target = section?.querySelector('.pdp-bundles') || this.anchor;
      if (!this.anchor) return;
      this.hidden = false;
      const update = () => this.classList.toggle('is-on', this.anchor.getBoundingClientRect().bottom < 0);
      window.addEventListener('scroll', update, { passive: true });
      update();
      this.querySelector('[data-sticky-go]')?.addEventListener('click', () => {
        const top = this.target.getBoundingClientRect().top + window.scrollY - 24;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }
  }
  if (!customElements.get('pdp-sticky-cta')) customElements.define('pdp-sticky-cta', PdpStickyCta);

  /* Generic scroller arrows: [data-scroll-prev]/[data-scroll-next] inside .pdp-scroll-root */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-scroll-prev],[data-scroll-next]');
    if (!btn) return;
    const root = btn.closest('.pdp-scroll-root');
    const sc = root?.querySelector('.pdp-scroller, .pdp-scroller-m');
    if (!sc) return;
    const item = sc.firstElementChild;
    const step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(sc).columnGap || 16) : sc.clientWidth;
    sc.scrollBy({ left: btn.hasAttribute('data-scroll-next') ? step : -step, behavior: 'smooth' });
  });

  /* Dialogs: [data-pdp-open="dialog-id"] opens <dialog id="dialog-id"> */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-pdp-open]');
    if (opener) {
      document.getElementById(opener.dataset.pdpOpen)?.showModal();
      return;
    }
    const closer = e.target.closest('[data-pdp-close]');
    if (closer) closer.closest('dialog')?.close();
    else if (e.target.tagName === 'DIALOG' && e.target.classList.contains('pdp-dialog')) e.target.close();
  });

  /* Reviews: reveal hidden reviews in steps */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pdp-more]');
    if (!btn) return;
    const root = btn.closest('.pdp-reviews');
    const hidden = [...root.querySelectorAll('.pdp-review[hidden]')];
    hidden.slice(0, +btn.dataset.pdpMore || 6).forEach((r) => (r.hidden = false));
    if (hidden.length <= (+btn.dataset.pdpMore || 6)) btn.remove();
  });

  /* Dropdowns (.pdp-acc, .pdp-faq__item): animated open/close. Items sharing data-acc-group stay one-open-at-a-time. */
  const ACC = '.pdp-acc, .pdp-faq__item';
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const setAcc = (item, open) => {
    if ((item.__open ?? item.open) === open) return;
    item.__open = open;
    item.classList.toggle('is-open', open);
    const body = item.querySelector(':scope > .pdp-faq__a, :scope > .pdp-acc__content');
    const from = item.open ? body.getBoundingClientRect().height : 0;
    item.__anim?.cancel();
    item.open = true;
    const done = () => {
      item.open = open;
      item.__open = undefined;
      item.__anim = null;
    };
    if (reduced()) return done();
    item.__anim = body.animate({ height: [`${from}px`, `${open ? body.scrollHeight : 0}px`] }, { duration: 300, easing: 'ease' });
    item.__anim.onfinish = done;
  };
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('summary');
    const item = summary?.parentElement;
    if (!item?.matches(ACC)) return;
    e.preventDefault();
    const open = !(item.__open ?? item.open);
    const group = item.dataset.accGroup;
    if (open && group) {
      document.querySelectorAll(`[data-acc-group="${group}"]`).forEach((other) => other !== item && setAcc(other, false));
    }
    setAcc(item, open);
  });

  /* Before/after slider */
  document.addEventListener('input', (e) => {
    if (!e.target.matches('.pdp-ba input')) return;
    e.target.closest('.pdp-ba').style.setProperty('--pos', `${e.target.value}%`);
  });

  /* Dots for horizontal scrollers: .pdp-scroll-root [data-dots] */
  const initDots = (root) => {
    root.querySelectorAll('.pdp-scroll-root [data-dots]').forEach((dots) => {
      if (dots.__init) return;
      dots.__init = true;
      const sc = dots.closest('.pdp-scroll-root').querySelector('.pdp-scroller, .pdp-scroller-m');
      if (!sc) return;
      let step = 1;
      let pages = 1;
      const build = () => {
        const gap = parseFloat(getComputedStyle(sc).columnGap) || 0;
        const first = sc.firstElementChild;
        step = first ? first.getBoundingClientRect().width + gap : sc.clientWidth;
        const perView = Math.max(1, Math.round((sc.clientWidth + gap) / step));
        pages = sc.scrollWidth > sc.clientWidth + 1 ? Math.max(1, sc.children.length - perView + 1) : 1;
        dots.replaceChildren(
          ...Array.from({ length: pages }, (_, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.setAttribute('aria-label', `Go to slide ${i + 1}`);
            b.addEventListener('click', () => sc.scrollTo({ left: i * step, behavior: 'smooth' }));
            return b;
          })
        );
        dots.hidden = pages < 2;
        sync();
      };
      const sync = () => {
        const i = Math.min(pages - 1, Math.round(sc.scrollLeft / step));
        [...dots.children].forEach((d, n) => d.classList.toggle('is-active', n === i));
      };
      sc.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
      window.addEventListener('resize', build);
      build();
    });
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const pad = (n) => String(n).padStart(2, '0');

  /* Scrolling bars: [data-pdp-speed] / [data-pdp-speed-m] in px per second. The track moves by half its width per loop. */
  const mobile = window.matchMedia('(max-width: 749px)');
  const setSpeeds = (root = document) => {
    root.querySelectorAll('[data-pdp-speed]').forEach((el) => {
      const pxs = +(mobile.matches ? el.dataset.pdpSpeedM : el.dataset.pdpSpeed) || 50;
      const dist = el.scrollWidth / 2;
      if (dist) el.style.setProperty('--speed', `${(dist / pxs).toFixed(2)}s`);
    });
  };
  let resizeRaf;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => setSpeeds());
  });

  const init = (root = document) => {
    initDots(root);
    setSpeeds(root);
    window.addEventListener('load', () => setSpeeds(root), { once: true });

    /* [data-pdp-date="N"]: today + N days as "October 28" */
    root.querySelectorAll('[data-pdp-date]').forEach((el) => {
      const d = new Date();
      d.setDate(d.getDate() + (+el.dataset.pdpDate || 0));
      el.textContent = `${months[d.getMonth()]} ${d.getDate()}`;
    });

    /* [data-pdp-timer]: counts down to midnight, or N minutes per visitor */
    root.querySelectorAll('[data-pdp-timer]').forEach((el) => {
      if (el.__timer) return;
      const mins = +el.dataset.pdpTimer;
      let end;
      if (mins > 0) {
        const key = `pdpTimer:${el.dataset.key || 'default'}`;
        try {
          end = +localStorage.getItem(key);
          if (!end || end < Date.now()) {
            end = Date.now() + mins * 60000;
            localStorage.setItem(key, end);
          }
        } catch (_) {
          end = Date.now() + mins * 60000;
        }
      } else {
        const m = new Date();
        m.setHours(24, 0, 0, 0);
        end = +m;
      }
      const tick = () => {
        const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
        el.textContent = `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
      };
      tick();
      el.__timer = setInterval(tick, 1000);
    });

    /* Looping videos play only while visible */
    const vids = root.querySelectorAll('video.pdp-autoplay');
    if (vids.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting && target.offsetParent !== null) {
            target.preload = 'auto';
            target.play().catch(() => {});
          } else target.pause();
        });
      }, { rootMargin: '200px 0px' });
      vids.forEach((v) => io.observe(v));
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
